import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Button, Card, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, FormGroup, FormControlLabel, Checkbox, IconButton,
  InputAdornment, Stack, TextField, Tooltip, Typography, Alert,
  FormControl, InputLabel, Select, MenuItem, FormHelperText,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Icon } from "@iconify/react";
import copyFill from "@iconify/icons-eva/copy-fill";
import closeFill from "@iconify/icons-eva/close-fill";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "src/react-query/queryKeys";
import { fetchWithError, fetchApi } from "src/react-query/fetchWithError";
import { apiKeysUrl, apiKeyByIdUrl } from "src/react-query/endPoints";
import { useDispatch } from "react-redux";
import { alertSlice } from "src/store/alert/alert.slice";
import { useIsGrandParent } from "src/hooks/useIsGrandParent";
import { useTeams } from "src/hooks/useTeams";
import Token from "src/util/Token";
import Page from "../Page";

const SCOPE_LABELS = {
  "chargers:read":     { label: "Chargers — Read",     color: "info" },
  "chargers:write":    { label: "Chargers — Write",    color: "warning" },
  "transactions:read": { label: "Transactions — Read", color: "info" },
  "remote:write":      { label: "Remote Operations",   color: "error" },
  "customers:read":    { label: "EV Drivers — Read",   color: "info" },
  "customers:write":   { label: "EV Drivers — Write",  color: "warning" },
  "pricegroups:read":  { label: "Price Groups — Read", color: "info" },
};

const ALL_SCOPES = Object.keys(SCOPE_LABELS);

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows?.z4,
  border: `1px solid ${theme.palette.grey[200]}`,
}));

// --- CopyButton ---
// Handles clipboard failure with a document.execCommand fallback so the
// button never gives false "Copied!" feedback when the Clipboard API fails.
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    let succeeded = false;
    try {
      await navigator.clipboard.writeText(text);
      succeeded = true;
    } catch {
      const el = document.createElement("textarea");
      el.value = text;
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.select();
      succeeded = document.execCommand("copy");
      document.body.removeChild(el);
    }
    // Only show "Copied!" if the copy actually succeeded
    if (succeeded) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Tooltip title={copied ? "Copied!" : "Copy"}>
      <IconButton size="small" onClick={handleCopy}>
        <Icon icon={copyFill} />
      </IconButton>
    </Tooltip>
  );
}

// --- KeyRevealDialog ---
// disableEscapeKeyDown + onClose={() => {}} prevent accidental closure before
// the user has copied the key. The button is the only dismissal path.
function KeyRevealDialog({ keyData, onClose, teamNameById }) {
  const teamLabel =
    keyData.teamId === null || keyData.teamId === undefined
      ? "Entire company (all teams)"
      : teamNameById?.[String(keyData.teamId)] || `Team #${keyData.teamId}`;
  return (
    <Dialog open maxWidth="sm" fullWidth disableEscapeKeyDown onClose={() => {}}>
      <DialogTitle>API Key Created</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Copy this key now — it will <strong>not</strong> be shown again.
        </Alert>
        <TextField
          fullWidth
          value={keyData.key}
          label="Your API Key"
          InputProps={{
            readOnly: true,
            sx: { fontFamily: "monospace", fontSize: 13 },
            endAdornment: (
              <InputAdornment position="end">
                <CopyButton text={keyData.key} />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Name: <strong>{keyData.name}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Scopes: {keyData.scopes.map((s) => SCOPE_LABELS[s]?.label || s).join(", ")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Access: <strong>{teamLabel}</strong>
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">I have copied the key</Button>
      </DialogActions>
    </Dialog>
  );
}

// --- RevokeConfirmDialog ---
// Replaces window.confirm — consistent with MUI design and non-blocking.
function RevokeConfirmDialog({ open, onConfirm, onCancel }) {
  return (
    <Dialog open={open} maxWidth="xs" fullWidth onClose={onCancel}>
      <DialogTitle>Revoke API Key?</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          Any integrations using this key will <strong>immediately stop working</strong>. This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" color="error" onClick={onConfirm}>Revoke</Button>
      </DialogActions>
    </Dialog>
  );
}

// --- CreateKeyDialog ---
function CreateKeyDialog({ onClose, onCreated, teams }) {
  const dispatch = useDispatch();
  const [name, setName] = useState("");
  const [selectedScopes, setSelectedScopes] = useState([]);
  // "" = no restriction (whole owning company). Otherwise the key is bound to
  // this single company/team and can only read/act on it.
  const [teamId, setTeamId] = useState("");

  const { mutate, isLoading } = useMutation(fetchApi, {
    onSuccess: (data) => onCreated(data.data),
    onError: (err) => {
      dispatch(alertSlice.actions.setAlert({
        open: true, severity: "error",
        message: err?.response?.data?.message ?? "Failed to create API key",
      }));
    },
  });

  const toggleScope = (scope) => {
    setSelectedScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    );
  };

  const handleCreate = () => {
    if (!name.trim()) {
      dispatch(alertSlice.actions.setAlert({ open: true, severity: "warning", message: "Please enter a name" }));
      return;
    }
    if (selectedScopes.length === 0) {
      dispatch(alertSlice.actions.setAlert({ open: true, severity: "warning", message: "Select at least one scope" }));
      return;
    }
    mutate({
      url: apiKeysUrl,
      method: "POST",
      body: {
        name: name.trim(),
        scopes: selectedScopes,
        // Only send teamId when a specific team is chosen; omitting it keeps the
        // default (whole-company) scope.
        ...(teamId !== "" ? { teamId } : {}),
      },
    });
  };

  return (
    <Dialog open maxWidth="sm" fullWidth onClose={onClose}>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <span>Generate New API Key</span>
          <IconButton size="small" onClick={onClose}><Icon icon={closeFill} /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth autoFocus
          label="Key Name"
          placeholder="e.g. My Integration, Dashboard App"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mt: 1, mb: 3 }}
        />
        <Typography variant="subtitle2" gutterBottom>Scopes (select what this key can access)</Typography>
        <FormGroup>
          {ALL_SCOPES.map((scope) => (
            <FormControlLabel
              key={scope}
              control={
                <Checkbox
                  checked={selectedScopes.includes(scope)}
                  onChange={() => toggleScope(scope)}
                />
              }
              label={
                <Chip
                  label={SCOPE_LABELS[scope].label}
                  color={SCOPE_LABELS[scope].color}
                  size="small"
                />
              }
            />
          ))}
        </FormGroup>

        <FormControl fullWidth sx={{ mt: 3 }}>
          <InputLabel id="apikey-team-label">Restrict access to</InputLabel>
          <Select
            labelId="apikey-team-label"
            label="Restrict access to"
            value={teamId}
            onChange={(e) => setTeamId(e.target.value)}
          >
            <MenuItem value="">
              <em>Entire company (no restriction)</em>
            </MenuItem>
            {Array.isArray(teams) &&
              teams.map((team) => (
                <MenuItem key={team.companyId} value={team.companyId}>
                  {team.displayName}
                </MenuItem>
              ))}
          </Select>
          <FormHelperText>
            Optional. Bind the key to a single company/team so it can only access
            that entity. Leave as “Entire company” for company-wide access.
          </FormHelperText>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>Cancel</Button>
        <Button variant="contained" onClick={handleCreate} disabled={isLoading}>
          {isLoading ? "Creating…" : "Create Key"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// --- ApiKeyList (main page) ---
export default function ApiKeyList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [revokeTargetId, setRevokeTargetId] = useState(null);

  // Super admin = user logged in with a Grand Parent (GP) company (from JWT).
  const isGrandParent = useIsGrandParent();

  // Owning GP company id (from JWT) — used to list teams a key can be bound to.
  const companyId = useMemo(() => {
    const parsed = Token.parsedToken();
    return parsed.success ? parsed.data?.companyId : null;
  }, []);
  const { data: teamsResp } = useTeams(companyId);
  const teams = useMemo(
    () => (Array.isArray(teamsResp?.data) ? teamsResp.data : []),
    [teamsResp]
  );
  const teamNameById = useMemo(() => {
    const map = {};
    teams.forEach((t) => {
      map[String(t.companyId)] = t.displayName;
    });
    return map;
  }, [teams]);

  useEffect(() => {
    if (!isGrandParent) {
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: "Not Authorized - Grand Parent (super admin) access required",
        })
      );
      navigate("/dashboard");
    }
  }, [isGrandParent, dispatch, navigate]);

  const { data: apiKeys = [], isFetching } = useQuery({
    queryKey: queryKeys.apiKeys(),
    queryFn: fetchWithError,
    select: (res) => res?.data ?? [],
    enabled: isGrandParent,
  });

  const { mutate: revokeKey } = useMutation(fetchApi, {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.apiKeys());
      dispatch(alertSlice.actions.setAlert({ open: true, severity: "success", message: "API key revoked" }));
    },
    onError: (err) => {
      dispatch(alertSlice.actions.setAlert({
        open: true, severity: "error",
        message: err?.response?.data?.message ?? "Failed to revoke key",
      }));
    },
  });

  const handleRevokeConfirm = () => {
    revokeKey({ url: apiKeyByIdUrl(revokeTargetId), method: "DELETE" });
    setRevokeTargetId(null);
  };

  const handleCreated = (keyData) => {
    setShowCreate(false);
    setNewKey(keyData);
    queryClient.invalidateQueries(queryKeys.apiKeys());
  };

  return (
    <Page>
      {showCreate && (
        <CreateKeyDialog
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
          teams={teams}
        />
      )}
      {newKey && (
        <KeyRevealDialog
          keyData={newKey}
          onClose={() => setNewKey(null)}
          teamNameById={teamNameById}
        />
      )}
      <RevokeConfirmDialog
        open={Boolean(revokeTargetId)}
        onConfirm={handleRevokeConfirm}
        onCancel={() => setRevokeTargetId(null)}
      />

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>API Keys</Typography>
          <Typography variant="body2" color="text.secondary">
            Programmatic access to CMS API. Keys are scoped — grant only what is needed.
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => setShowCreate(true)}>
          + Generate Key
        </Button>
      </Stack>

      {isFetching && <Typography color="text.secondary">Loading…</Typography>}

      {!isFetching && apiKeys.length === 0 && (
        <StyledCard>
          <Typography color="text.secondary" align="center" py={4}>
            No API keys yet. Click "Generate Key" to create your first one.
          </Typography>
        </StyledCard>
      )}

      <Stack spacing={2}>
        {apiKeys.map((key) => {
          const isActive = key.active === 1;
          // API returns scopes as an array; fall back to [] rather than JSON.parse
          // so a malformed/null value can never crash the list render
          const scopes = Array.isArray(key.scopes) ? key.scopes : [];
          const teamLabel =
            key.team_id === null || key.team_id === undefined
              ? "All teams"
              : teamNameById[String(key.team_id)] || `Team #${key.team_id}`;
          return (
            <StyledCard key={key.key_id}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ sm: "center" }}
                spacing={2}
              >
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle1" fontWeight={700}>{key.name}</Typography>
                    <Chip
                      label={isActive ? "Active" : "Revoked"}
                      color={isActive ? "success" : "default"}
                      size="small"
                    />
                    <Chip
                      label={teamLabel}
                      variant="outlined"
                      color={
                        key.team_id === null || key.team_id === undefined
                          ? "default"
                          : "primary"
                      }
                      size="small"
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                    {key.key_prefix}••••••••••••••••••••••••••••••••••••••••
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {scopes.map((scope) => (
                      <Chip
                        key={scope}
                        label={SCOPE_LABELS[scope]?.label || scope}
                        color={SCOPE_LABELS[scope]?.color || "default"}
                        size="small"
                        sx={{ mr: 0.5, mb: 0.5 }}
                      />
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Created {new Date(key.created_at).toLocaleDateString()}
                    {key.last_used_at && ` · Last used ${new Date(key.last_used_at).toLocaleDateString()}`}
                    {key.expires_at && ` · Expires ${new Date(key.expires_at).toLocaleDateString()}`}
                  </Typography>
                </Box>
                {isActive && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => setRevokeTargetId(key.key_id)}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    Revoke
                  </Button>
                )}
              </Stack>
            </StyledCard>
          );
        })}
      </Stack>
    </Page>
  );
}
